import {
  Box,
  Typography,
  Stack,
  useMediaQuery,
  TextField,
  Dialog,
  DialogContent,
  IconButton,
  Fab,
  Tooltip,
  Grid,
  Menu,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../../Components/Common/Header";
import SearchBox from "../../Components/Search/SearchBox";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { DoneSharp, KeyboardArrowDown } from "@mui/icons-material";
import Footer from "../../Components/Common/Footer";
import axios from "axios";
import config from "../../config/config";
import { motion } from "framer-motion";
import SearchCard, { CompactSearchProductCard } from "../../Components/Search/SearchCard";
import { logUserAction, createSearchFeedback } from "../../utils/analytics";
import NewOnSaleProductCard from "../../Components/Product/NewOnSaleProductCard";

const GenderDropdown = ({ gender, setGender }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (anchorEl) {
        setAnchorEl(null);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [anchorEl]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        mt: 2,
        mb: 0.5,
        px: 2,
      }}
    >
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          background: "#FF9B80",
          borderRadius: "100px",
          px: 2.5,
          py: "4px",
          color: "#fff",
          fontFamily: "Poppins-Medium",
          fontSize: { xs: "12px", sm: "13px" },
          fontWeight: 500,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          boxShadow: "0 2px 8px rgba(255,107,0,0.2)",
          cursor: "pointer",
          userSelect: "none"
        }}
      >
        Searching for {gender} <KeyboardArrowDown sx={{ ml: 0.5, fontSize: "16px" }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        disableScrollLock={true}
        transitionDuration={0}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            minWidth: "160px",
            backgroundColor: "#FF9B80",
          }
        }}
      >
        {["Women", "Men", "Kids"].filter(g => g !== gender).map((g) => (
          <MenuItem
            key={g}
            onClick={() => {
              setAnchorEl(null);
              setGender(g);
            }}
            sx={{
              fontFamily: "Poppins-Medium",
              fontSize: "14px",
              color: "#fff",
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.2)",
              }
            }}
          >
            Search for {g}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const ResearchStepsViewer = ({ allResearchSteps, loading, intervalComplete, setIntervalComplete }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (loading) {
      setCurrentMessageIndex(0);
    }
  }, [loading]);

  useEffect(() => {
    if (loading || !intervalComplete) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + "." : ""));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [loading, intervalComplete]);

  useEffect(() => {
    // If there are no steps and loading finished, complete immediately
    if (!loading && (!allResearchSteps || allResearchSteps.length === 0) && !intervalComplete) {
      setIntervalComplete(true);
      return;
    }

    if (allResearchSteps && allResearchSteps.length > 0 && !intervalComplete) {
      // Pace the steps based on loading state. Slowly progress while loading.
      const delay = loading ? 7000 : 400;
      
      const timeout = setTimeout(() => {
        if (currentMessageIndex < allResearchSteps.length - 1) {
          setCurrentMessageIndex(currentMessageIndex + 1);
        } else if (!loading) {
          setIntervalComplete(true);
        }
      }, delay);
      
      return () => clearTimeout(timeout);
    }
  }, [loading, allResearchSteps, intervalComplete, currentMessageIndex, setIntervalComplete]);

  return (
    <Typography
      component={motion.p}
      key={currentMessageIndex}
      initial={{ opacity: 0, x: -5 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      sx={{
        fontFamily: "Poppins-Medium",
        fontSize: "14px",
        fontWeight: 500,
        color: "#FF6B00",
        letterSpacing: "0.2px",
      }}
    >
      {allResearchSteps && allResearchSteps.length > 0
        ? allResearchSteps[currentMessageIndex]
        : `Searching${dots}`}
    </Typography>
  );
};

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const searchCalledRef = React.useRef(false);

  const urlQuery = location.state?.search || location.state?.q || "";
  const urlGender = location.state?.gender || "Women";


  const getInitialState = (key, defaultVal) => {
    try {
      const query = localStorage.getItem("query");
      const savedGender = localStorage.getItem("gender");
      if (query === urlQuery && savedGender === urlGender) {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultVal;
      }
    } catch (e) { }
    return defaultVal;
  };

  const [search, setSearch] = useState(urlQuery);
  const [gender, setGender] = useState(urlGender);
  const [searchResults, setSearchResults] = useState(() => getInitialState("searchResult", []));

  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const initialSteps = getInitialState("researchSteps", []);


  const [categorySelect, setCategorySelect] = useState(() => getInitialState("categorySelect", "handpik"));
  const [categoryCollection, setCategoryCollection] = useState(() => getInitialState("currentCategoryProducts", []));
  const [allCategories, setAllCategories] = useState(() => getInitialState("categoryCollection", []));
  const [allResearchSteps, setAllResearchSteps] = useState(initialSteps);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [intervalComplete, setIntervalComplete] = useState(() => {
    try {
      if (localStorage.getItem("query") === urlQuery && localStorage.getItem("gender") === urlGender && localStorage.getItem("searchResult")) return true;
    } catch (e) { }
    return false;
  });

  const [noResultAvailable, setNoResultAvailable] = useState(() => {
    try {
      if (localStorage.getItem("query") === urlQuery && localStorage.getItem("gender") === urlGender) {
        const res = localStorage.getItem("searchResult");
        if (res && JSON.parse(res).length === 0) return true;
      }
    } catch (e) { }
    return false;
  });
  const [ipInfo, setIpInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState(urlQuery);

  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const trackingContext = React.useMemo(() => ({
    source: "text_search",
    query: searchQuery || urlQuery || "",
    category: categorySelect || "handpik",
    gender: gender || urlGender || "Women",
  }), [searchQuery, urlQuery, categorySelect, gender, urlGender]);

  React.useEffect(() => {
    if (!intervalComplete) return;

    let productsToLog = [];
    if (categorySelect === "handpik" || categoryCollection?.length === 0) {
      if (searchResults && searchResults.length > 0) {
        productsToLog = searchResults.flatMap(brand => brand.products || []);
      }
    } else {
      if (categoryCollection && categoryCollection.length > 0) {
        productsToLog = categoryCollection;
      }
    }

    if (productsToLog.length > 0) {
      import("../../utils/analytics").then(({ logBatchUserAction }) => {
        const uniqueBrands = [...new Set(productsToLog.map(p => p.brand).filter(Boolean))];
        if (uniqueBrands.length > 0) {
          logBatchUserAction(uniqueBrands.map(brand => ({
            action: "brand_impression",
            data: {
              brand: brand,
              ...trackingContext
            }
          })));
        }
      });
    }
  }, [categorySelect, categoryCollection, searchResults, intervalComplete, trackingContext]);

  const [feedback, setFeedback] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [researchExpanded, setResearchExpanded] = React.useState(true);
  const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);

  const getPercentage = (salePercentage) => {
    return (
      Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(salePercentage * 100) + "%"
    );
  };

  const getSearch = async (customGender, customSearch) => {
    const activeGender = customGender || gender;
    const activeSearch = customSearch !== undefined ? customSearch : search;
    if (activeSearch === "") return;
    setCategoryCollection([]);
    setSearchResults([]);
    setNoResultAvailable(false);

    try {
      setSearchLoading(true);
      localStorage.setItem("query", activeSearch);
      localStorage.setItem("gender", activeGender);
      const searchPromise = axios.get(
        `${config.backendUrl}/inventory/search?q=${encodeURIComponent(activeSearch)}&gender=${encodeURIComponent(activeGender)}`,
      );

      let ipPromise = Promise.resolve(ipInfo);
      if (!ipInfo) {
        ipPromise = Promise.race([
          axios.get("https://ipapi.co/json?access_key=e8d395e0f2c259f5c4b281680485dbd8")
            .then(res => ({ city: res.data?.city, country: res.data?.country_name }))
            .catch(() => null),
          new Promise(resolve => setTimeout(() => resolve(null), 1500))
        ]);
      }

      const searchResult = await searchPromise;

      if (searchResult.status === 200) {
        const categories = searchResult.data?.results || [];

        setSearchResults(categories);
        setNoResultAvailable(categories.length === 0);
        if (categories.length > 0) {
          setIntervalComplete(true);
        }

        ipPromise.then((resolvedIp) => {
          if (!ipInfo && resolvedIp) {
            setIpInfo(resolvedIp);
          }
          logUserAction({
            action: "search",
            data: {
              keyword: activeSearch,
              gender: activeGender,
              location:
                resolvedIp?.city === undefined || resolvedIp?.country === undefined
                  ? null
                  : `${resolvedIp?.city}, ${resolvedIp?.country}`,
            },
            timestamp: new Date().toISOString(),
          });
        });

        await localStorage.setItem("searchResult", JSON.stringify(categories));

        return categories;
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setSearchLoading(false);
    }
    return [];
  };

  const postSearchFeedback = async () => {
    await createSearchFeedback(feedback, reason)
      .then(() => {
        console.log("Feedback submitted");
      })
      .catch((error) => {
        console.error("Error submitting feedback:", error);
      });
  };

  const getCollectionsByCategory = async (customGender, customSearch) => {
    const activeGender = customGender || gender;
    const activeSearch = customSearch !== undefined ? customSearch : search;
    const searchResult = await axios.get(
      `${config.backendUrl}/inventory/list_of_categories?q=${activeSearch}&gender=${encodeURIComponent(activeGender)}`,
    );
    setAllCategories(
      searchResult.data?.results.sort((a, b) =>
        a.name === "On Sale" ? -1 : b.name === "On Sale" ? 1 : 0,
      ),
    );
    await localStorage.setItem(
      "categoryCollection",
      JSON.stringify(searchResult.data?.results),
    );
    // const sortedCategories = categories.sort((a, b) => (a.name === "On Sale" ? -1 : b.name === "On Sale" ? 1 : 0));
    // console.log("response", searchResult.data?.collections);
  };

  const getResearchSteps = async (customGender, customSearch) => {
    const activeGender = typeof customGender === 'string' ? customGender : gender;
    const activeSearch = customSearch !== undefined ? customSearch : search;
    if (!activeSearch) return;

    navigate('/search', { state: { search: activeSearch, gender: activeGender }, replace: true });

    localStorage.removeItem("query");
    localStorage.removeItem("gender");
    localStorage.removeItem("searchResult");
    localStorage.removeItem("categoryCollection");
    localStorage.removeItem("researchSteps");
    localStorage.removeItem("categorySelect");
    localStorage.removeItem("currentCategoryProducts");

    searchCalledRef.current = true;

    setAllResearchSteps([]);
    setSearchResults([]);
    setCategorySelect("handpik");
    setCategoryCollection([]);
    setAllCategories([]);
    setIntervalComplete(false);

    setNoResultAvailable(false);
    setSearchQuery(activeSearch);
    setResearchExpanded(true);

    try {
      setLoading(true);
      const researchStepsPromise = axios.get(
        `${config.backendUrl}/inventory/research_steps?q=${encodeURIComponent(activeSearch)}&gender=${encodeURIComponent(activeGender)}`,
      ).then((res) => {
        if (res.status === 200) {
          const steps = res.data?.results?.research_steps || [];
          setAllResearchSteps(steps);
          localStorage.setItem("researchSteps", JSON.stringify(steps));
        }
      }).catch((err) => {
        console.error("Error fetching research steps:", err);
      });

      // Call research steps, brand search, and category search all in parallel
      await Promise.all([
        researchStepsPromise,
        getSearch(activeGender, activeSearch),
        getCollectionsByCategory(activeGender, activeSearch),
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    if (searchCalledRef.current) {
      console.log("Search already in progress, skipping duplicate call");
      return;
    }

    const query = await localStorage.getItem("query");
    const savedGender = await localStorage.getItem("gender");
    const searchResult = await localStorage.getItem("searchResult");
    const categoryCollection = await localStorage.getItem("categoryCollection");
    const researchSteps = await localStorage.getItem("researchSteps");

    if (searchResult && query === search && savedGender === gender && search.trim() !== "") {
      console.log("Found in localStorage");
      const parsedResults = JSON.parse(searchResult);
      setSearchResults(parsedResults);

      if (categoryCollection) {
        setAllCategories(JSON.parse(categoryCollection));
      }

      if (researchSteps) {
        const parsedSteps = JSON.parse(researchSteps);
        setAllResearchSteps(parsedSteps);

      }

      setIntervalComplete(true);
      if (parsedResults.length === 0) {
        setNoResultAvailable(true);
      }
    } else if (search.trim() !== "" && !searchCalledRef.current) {
      console.log("Couldn't find in localStorage or query mismatch");
      getResearchSteps();
    }
  };

  useEffect(() => {
    if (!location.state?.gender) {
      navigate("/");
      return;
    }
    const locQuery = location.state?.search || location.state?.q || "";
    if (locQuery.trim() !== "" && locQuery === search && !searchCalledRef.current) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    const locQuery = location.state?.search || location.state?.q || "";
    if (locQuery !== search) {
      setSearch(locQuery);
      setSearchQuery(locQuery);
      if (locQuery.trim() !== "") {
        setCategorySelect("handpik");
        setCategoryCollection([]);
        if (!searchCalledRef.current) {
          fetchData();
        }
      } else {
        setSearchResults([]);
        setAllCategories([]);
        setAllResearchSteps([]);
        setNoResultAvailable(false);
        setIntervalComplete(true);
      }
    }
  }, [location.key]);


  // Sync gender from state when navigating back/forward, and enforce gender presence
  useEffect(() => {
    if (!location.state?.gender) {
      navigate("/");
      return;
    }
    const locGender = location.state?.gender || "Women";
    if (locGender !== gender) {
      setGender(locGender);
    }
  }, [location.key]);



  useEffect(() => {
    if (intervalComplete || noResultAvailable) {
      searchCalledRef.current = false;
    }
  }, [intervalComplete, noResultAvailable]);

  useEffect(() => {
    axios
      .get("https://ipapi.co/json?access_key=e8d395e0f2c259f5c4b281680485dbd8")
      .then(async (data) => {
        setIpInfo({
          city: data.data?.city,
          country: data.data?.country_name,
        });
      })
      .catch((err) => console.error("Error fetching IP:", err));
  }, []);

  // Auto-collapse research when results are displayed
  useEffect(() => {
    if (intervalComplete && searchResults?.length > 0) {
      setResearchExpanded(false);

      // Only auto-scroll if this was a fresh search, NOT a browser Back navigation
      if (navigationType !== "POP") {
        // Scroll down just enough to hide the search box
        setTimeout(() => {
          const searchBox = document.querySelector('[class*="MuiBox"]');
          if (searchBox) {
            window.scrollTo({
              top: 150,
              behavior: 'smooth'
            });
          }
        }, 300);
      }
    }
  }, [intervalComplete, searchResults, navigationType]);

  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const mediumScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "lg"),
  );

  return (
    <Box sx={{
      backgroundColor: "#FFFFFF",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />

      {/* ─── Active Gender Indicator ───────────────────────────────────────── */}
      <GenderDropdown gender={gender} setGender={setGender} />

      <SearchBox
        search={search}
        setSearch={setSearch}
        startSearch={(newSearch) => getResearchSteps(null, newSearch)}
        loading={loading}
        searchLoading={searchLoading}
      />


      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "100%", lg: "1600px", xl: "1920px" },
            width: "100%",
            mx: "auto",
            px: { xs: 1.5, sm: 2.5, md: 4, lg: 5 },
            pt: 0,
            pb: 1,
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {/* Sleek Dynamic Search Status Badge */}
            {!intervalComplete && (loading || allResearchSteps?.length > 0) && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  py: 1.25,
                  mb: 2,
                  background: "linear-gradient(135deg, rgba(255,107,0,0.08) 0%, rgba(255,107,0,0.02) 100%)",
                  border: "1px solid rgba(255,107,0,0.2)",
                  borderRadius: "100px",
                  boxShadow: "0 4px 12px rgba(255,107,0,0.05)",
                  maxWidth: "100%",
                }}
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #FF6B00 0%, #FFA785 100%)",
                    boxShadow: "0 0 8px rgba(255,107,0,0.6)",
                    animation: "searchPulse 1.5s ease-in-out infinite",
                    "@keyframes searchPulse": {
                      "0%, 100%": { transform: "scale(0.8)", opacity: 0.5 },
                      "50%": { transform: "scale(1.2)", opacity: 1 },
                    },
                  }}
                />
                <ResearchStepsViewer
                  allResearchSteps={allResearchSteps}
                  loading={loading}
                  intervalComplete={intervalComplete}
                  setIntervalComplete={setIntervalComplete}
                />
              </Box>
            )}
          </Box>

          {intervalComplete && searchResults?.length !== 0 && (
            <>
              {/* Sticky Category Section */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 99,
                  backgroundColor: "#fff",
                  pt: 1,
                  pb: 0.5,
                  mt: 0.5,
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: smallScreen ? 1.25 : 1.5,
                    overflowX: "auto",
                    overflowY: "visible",
                    pb: 1.5,
                    pt: 1.5,
                    px: smallScreen ? 2 : 3,
                    justifyContent: { md: "center" },
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    scrollbarWidth: "none",
                  }}
                >
                  <Box
                    onClick={() => {
                      setCategorySelect("handpik");
                      setCategoryCollection([]);
                      localStorage.setItem("categorySelect", JSON.stringify("handpik"));
                      localStorage.setItem("currentCategoryProducts", JSON.stringify([]));
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.75,
                      minWidth: smallScreen ? "70px" : "75px",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: { xs: "52px", sm: "56px", xl: "68px" },
                        height: { xs: "52px", sm: "56px", xl: "68px" },
                        borderRadius: "50%",
                        background: categorySelect === "handpik"
                          ? "linear-gradient(135deg, #FF6B00 0%, #FFA785 100%)"
                          : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: categorySelect === "handpik"
                          ? "0 4px 16px rgba(255,107,0,0.3)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                        border: categorySelect === "handpik"
                          ? "2px solid #FF6B00"
                          : "2px solid rgba(0,0,0,0.08)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        animation: "categoryPulse 2s ease-in-out infinite",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "-4px",
                          left: "-4px",
                          right: "-4px",
                          bottom: "-4px",
                          borderRadius: "50%",
                          border: "2px solid",
                          borderColor: categorySelect === "handpik"
                            ? "#FF6B00"
                            : "#9AE0EB",
                          opacity: 0,
                          animation: "ripple 1.5s ease-out infinite",
                        },
                        "@keyframes categoryPulse": {
                          "0%, 100%": {
                            transform: "scale(1)",
                            boxShadow: categorySelect === "handpik"
                              ? "0 4px 16px rgba(255,107,0,0.3)"
                              : "0 2px 8px rgba(0,0,0,0.08)",
                          },
                          "50%": {
                            transform: "scale(1.05)",
                            boxShadow: categorySelect === "handpik"
                              ? "0 6px 20px rgba(255,107,0,0.5)"
                              : "0 4px 12px rgba(0,0,0,0.12)",
                          },
                        },
                        "@keyframes ripple": {
                          "0%": {
                            transform: "scale(1)",
                            opacity: 0.8,
                          },
                          "100%": {
                            transform: "scale(1.4)",
                            opacity: 0,
                          },
                        },
                      }}
                    >
                      <img
                        src={require("../../assets/images/login-handpik-logo_cropped.png")}
                        alt="Handpik.ai"
                        style={{
                          width: "60%",
                          height: "60%",
                          objectFit: "contain",
                          filter: categorySelect === "handpik" ? "brightness(0) invert(1)" : "none",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: "Poppins-SemiBold",
                        fontSize: { xs: "10px", sm: "11px", xl: "13px" },
                        fontWeight: 600,
                        color: categorySelect === "handpik" ? "#1a1a1a" : "#666",
                        textAlign: "center",
                        transition: "color 0.3s ease",
                      }}
                    >
                      Handpicked
                    </Typography>
                  </Box>

                  {intervalComplete && allCategories?.length > 0
                    ? allCategories.map(
                      (item, index) =>
                        item?.products.length > 0 && (
                          <Box
                            key={index}
                            onClick={() => {
                              setCategorySelect(item?.name);
                              setCategoryCollection(item?.products);
                              localStorage.setItem("categorySelect", JSON.stringify(item?.name));
                              localStorage.setItem("currentCategoryProducts", JSON.stringify(item?.products));

                              logUserAction({
                                action: "category_click",
                                data: {
                                  category: item?.name || null,
                                },
                                timestamp: new Date().toISOString(),
                              });
                            }}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.75,
                              minWidth: { xs: "70px", sm: "75px", xl: "90px" },
                              cursor: "pointer",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                transform: "translateY(-3px)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                position: "relative",
                                width: { xs: "52px", sm: "56px", xl: "68px" },
                                height: { xs: "52px", sm: "56px", xl: "68px" },
                                borderRadius: "50%",
                                overflow: "visible",
                                backgroundColor: item?.name === "On Sale" ? "#fff" : "transparent",
                                border: categorySelect === item?.name
                                  ? "2px solid #FF6B00"
                                  : "2px solid rgba(0,0,0,0.08)",
                                boxShadow: categorySelect === item?.name
                                  ? "0 4px 16px rgba(255,107,0,0.3)"
                                  : "0 2px 8px rgba(0,0,0,0.08)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                animation: "categoryPulse 2s ease-in-out infinite",
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  top: "-4px",
                                  left: "-4px",
                                  right: "-4px",
                                  bottom: "-4px",
                                  borderRadius: "50%",
                                  border: "2px solid",
                                  borderColor: categorySelect === item?.name
                                    ? "#FF6B00"
                                    : "#9AE0EB",
                                  opacity: 0,
                                  animation: "ripple 1.5s ease-out infinite",
                                  zIndex: -1,
                                },
                                "@keyframes categoryPulse": {
                                  "0%, 100%": {
                                    transform: "scale(1)",
                                    boxShadow: categorySelect === item?.name
                                      ? "0 4px 16px rgba(255,107,0,0.3)"
                                      : "0 2px 8px rgba(0,0,0,0.08)",
                                  },
                                  "50%": {
                                    transform: "scale(1.05)",
                                    boxShadow: categorySelect === item?.name
                                      ? "0 6px 20px rgba(255,107,0,0.5)"
                                      : "0 4px 12px rgba(0,0,0,0.12)",
                                  },
                                },
                                "@keyframes ripple": {
                                  "0%": {
                                    transform: "scale(1)",
                                    opacity: 0.8,
                                  },
                                  "100%": {
                                    transform: "scale(1.4)",
                                    opacity: 0,
                                  },
                                },
                              }}
                            >
                              <img
                                src={
                                  item?.name === "On Sale"
                                    ? require("../../assets/images/onsale.png")
                                    : item?.products[0]?.image_url
                                }
                                onError={(e) => {
                                  if (item?.name === "On Sale") return;
                                  const fallbackImage = item?.products[0]?.images?.[0];
                                  if (fallbackImage && !e.target.dataset.triedFallback) {
                                    e.target.dataset.triedFallback = "true";
                                    e.target.src = fallbackImage;
                                  } else {
                                    e.target.onerror = null;
                                    e.target.src = require("../../assets/images/no_image.png");
                                  }
                                }}
                                alt={item?.name}
                                style={{
                                  width: item?.name === "On Sale" ? "70%" : "100%",
                                  height: item?.name === "On Sale" ? "70%" : "100%",
                                  objectFit: "contain",
                                  borderRadius: "50%",
                                  position: item?.name === "On Sale" ? "absolute" : "static",
                                  top: item?.name === "On Sale" ? "50%" : "auto",
                                  left: item?.name === "On Sale" ? "50%" : "auto",
                                  transform: item?.name === "On Sale" ? "translate(-50%, -50%)" : "none",
                                }}
                              />

                              {/* Selected overlay gradient */}
                              {categorySelect === item?.name && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    borderRadius: "50%",
                                    background: "linear-gradient(135deg, rgba(255,183,156,0.25) 0%, rgba(255,167,133,0.25) 100%)",
                                    zIndex: 1,
                                  }}
                                />
                              )}
                            </Box>

                            <Typography
                              sx={{
                                fontFamily: "Poppins-SemiBold",
                                fontSize: { xs: "10px", sm: "11px", xl: "13px" },
                                fontWeight: 600,
                                color: categorySelect === item?.name ? "#1a1a1a" : "#666",
                                textAlign: "center",
                                transition: "color 0.3s ease",
                                maxWidth: { xs: "70px", sm: "75px", xl: "90px" },
                              }}
                            >
                              {item?.name}
                            </Typography>
                          </Box>
                        ),
                    )
                    : ""}
                </Box>
              </Box>
              {/* End Sticky Category Section */}
            </>
          )}

          {/* Card */}
          {intervalComplete &&
            categoryCollection.length === 0 &&
            searchResults?.length > 0 && (
              <SearchCard searchResults={searchResults} trackingContext={trackingContext} />
            )}

          {noResultAvailable && !searchLoading && (
            <Box
              sx={{
                borderRadius: "20px",
                backgroundColor: "#fff",
                boxShadow: "-1px -2px 15px 0px #00000012",
                p: 2,
                m: 2,
                overflowX: "visible",
                position: "relative",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold",
                  fontSize: "14.21px",
                  fontWeight: "600",
                  color: "#000",
                }}
              >
                Unfortunately, I couldn't find any relevant products meeting your
                requirements
              </Typography>
            </Box>
          )}

          {categoryCollection?.length > 0 && (
            <Box sx={{ px: { xs: 0, sm: 1, md: 2 }, pb: 2 }}>
              <Grid container spacing={2}>
                {categoryCollection.map((product, index) => (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2} key={index}>
                    <Box sx={{ height: { xs: "260px", sm: "300px", md: "360px", lg: "380px" }, width: "100%" }}>
                      <CompactSearchProductCard product={product} trackingContext={trackingContext} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          {/* Inline Feedback Button */}
          {intervalComplete && searchResults?.length > 0 && !submitted && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
              <Box
                onClick={() => setFeedbackModalOpen(true)}
                sx={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  background: "linear-gradient(135deg, #fff 0%, #f9f9f9 100%)",
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: "50px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                <Typography sx={{ fontSize: { xs: "18px", sm: "20px" } }}>💭</Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins-SemiBold",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Share Feedback
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Feedback Modal */}
      <Dialog
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: smallScreen ? 2 : 3,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: smallScreen ? "18px" : "20px",
                  fontFamily: "Poppins-Bold",
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                How were these results?
              </Typography>
              <IconButton
                onClick={() => setFeedbackModalOpen(false)}
                sx={{
                  color: "#666",
                  "&:hover": { color: "#1a1a1a" },
                }}
              >
                ✕
              </IconButton>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Box
                onClick={() => {
                  setFeedback("up");
                  setSubmitted(true);
                  postSearchFeedback("up", "");
                  setTimeout(() => setFeedbackModalOpen(false), 1000);
                }}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontFamily: "Poppins-Medium",
                  fontWeight: 500,
                  background: feedback === "up"
                    ? "linear-gradient(135deg, #ABCC8C 0%, #95B87A 100%)"
                    : "#f5f5f5",
                  color: feedback === "up" ? "#fff" : "#666",
                  border: feedback === "up" ? "none" : "1px solid rgba(0,0,0,0.06)",
                  boxShadow: feedback === "up" ? "0 2px 8px rgba(171,204,140,0.3)" : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  textAlign: "center",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: feedback === "up"
                      ? "0 4px 12px rgba(171,204,140,0.4)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                👍 Helpful
              </Box>

              <Box
                onClick={() => setFeedback("down")}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  px: 2.5,
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontFamily: "Poppins-Medium",
                  fontWeight: 500,
                  background: feedback === "down"
                    ? "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)"
                    : "#f5f5f5",
                  color: feedback === "down" ? "#fff" : "#666",
                  border: feedback === "down" ? "none" : "1px solid rgba(0,0,0,0.06)",
                  boxShadow: feedback === "down" ? "0 2px 8px rgba(255,183,156,0.3)" : "none",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  textAlign: "center",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: feedback === "down"
                      ? "0 4px 12px rgba(255,183,156,0.4)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                👎 Not helpful
              </Box>
            </Stack>

            {feedback === "down" && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  size="small"
                  placeholder="Tell us what went wrong…"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={3}
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontFamily: "Poppins-Regular",
                      fontSize: "14px",
                      backgroundColor: "#f9f9f9",
                      "&:hover": {
                        backgroundColor: "#fff",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                <Box
                  onClick={() => {
                    if (!reason.trim()) return;
                    setSubmitted(true);
                    postSearchFeedback();
                    setTimeout(() => setFeedbackModalOpen(false), 1000);
                  }}
                  sx={{
                    alignSelf: "flex-end",
                    cursor: reason.trim() ? "pointer" : "not-allowed",
                    background: reason.trim()
                      ? "linear-gradient(135deg, #FFB79C 0%, #FFA785 100%)"
                      : "#e0e0e0",
                    color: "#fff",
                    px: 3,
                    py: 1,
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontFamily: "Poppins-Medium",
                    fontWeight: 500,
                    boxShadow: reason.trim() ? "0 2px 8px rgba(255,183,156,0.3)" : "none",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: reason.trim() ? "translateY(-1px)" : "none",
                      boxShadow: reason.trim()
                        ? "0 4px 12px rgba(255,183,156,0.4)"
                        : "none",
                    },
                    "&:active": {
                      transform: reason.trim() ? "translateY(0)" : "none",
                    },
                  }}
                >
                  Submit Feedback
                </Box>
              </Box>
            )}

            {submitted && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #ABCC8C 0%, #95B87A 100%)",
                  color: "#fff",
                  textAlign: "center",
                  fontFamily: "Poppins-Medium",
                }}
              >
                ✓ Thank you for your feedback!
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default Search;
